import { Maybe, maybe } from '@passionware/monads';
import { cva } from 'class-variance-authority';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';
import { useFocusWithin } from 'v5/platform/react/useFocusWithin';
import { InputSize, InputVariant } from 'v5/design-system/Input';
import { getCountryCallingCode, switchCountryCode } from './country';
import { CountryInput } from './CountryInput';
import { PhoneInput } from './PhoneInput';

export type InternationalPhoneValue = {
  countryCode: CountryCode | null;
  rawValue: string;
};
export interface InternationalPhoneInputProps {
  className?: string;
  value: InternationalPhoneValue;
  onChange?: (value: InternationalPhoneValue | null | undefined) => void;
  onBlur?: () => void;
  disabled?: boolean;
  name?: string;
  variant?: InputVariant;
  size?: InputSize;
  hasError?: boolean;
  placeholder?: string;
  readOnly?: boolean;
}

const internationalPhoneInputVariants = cva('flex items-center', {
  variants: {
    variant: {
      regular: 'gap-2',
      inline: 'gap-0',
      plain: 'gap-0'
    },
    size: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
      xl: ''
    }
  },
  compoundVariants: [
    {
      variant: ['inline', 'plain'],
      class: '*:first:pr-1 *:last:pl-2'
    }
  ]
});

interface InternationalPhoneLayoutProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'size'> {
  variant?: InputVariant;
  size?: InputSize;
}

/**
 * Layout component for international phone input.
 * Handles the flex container styling for country selector and phone input.
 */
export const InternationalPhoneLayout = React.forwardRef<
  HTMLDivElement,
  InternationalPhoneLayoutProps
>(function InternationalPhoneLayout(
  { children, className, variant = 'regular', size = 'md', ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      {...rest}
      className={cn(
        internationalPhoneInputVariants({ variant, size, className })
      )}
    >
      {children}
    </div>
  );
});

/**
 * A combined international phone input with country selector.
 * Manages the country code and phone number in a single value.
 * The input is freeform - the country selector only swaps country codes.
 *
 * @example
 * ```tsx
 * <InternationalPhoneInput
 *   value={phoneNumber}
 *   onChange={setPhoneNumber}
 *   defaultCountry="GB"
 * />
 * ```
 */
export function InternationalPhoneInput({
  className,
  value,
  onChange,
  onBlur,
  disabled,
  name,
  variant = 'regular',
  size = 'md',
  hasError = false,
  placeholder,
  readOnly = false
}: InternationalPhoneInputProps) {
  const emitChange = React.useCallback(
    (rawValue?: string) => {
      if (!onChange) return;

      const trimmed = rawValue?.trim();
      if (!trimmed) {
        onChange(null);
        return;
      }

      onChange(toStructuredValue(trimmed, value.countryCode));
    },
    [onChange, value]
  );

  const handleCountryChange = (newCountry: CountryCode | undefined) => {
    if (!newCountry) return;
    const dialCode = getCountryCallingCode(newCountry);
    const switched = value.rawValue
      ? switchCountryCode(value.rawValue, newCountry)
      : dialCode
        ? `+${dialCode}`
        : '';

    const updated = toStructuredValue(switched, newCountry) ?? value;

    onChange?.(updated);
  };

  const focusWithin = useFocusWithin({ onBlurWithin: onBlur });

  // Handle paste events for phone numbers
  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');

      // Check if it looks like a phone number (contains mostly digits)
      const digitsOnly = pastedText.replace(/\D/g, '');
      if (pastedText && digitsOnly.length >= 5) {
        // Prevent default paste behavior
        e.preventDefault();

        if (pastedText.startsWith('+')) {
          // International format - use as is
          onChange?.(toStructuredValue(pastedText, null));
          return;
        } else {
          // National format - prepend current country dial code
          const dialCode = getCountryCallingCode(
            toStructuredValue(pastedText, value.countryCode).countryCode ??
              undefined
          );

          if (dialCode) {
            // Remove leading zero from national format (common in UK, IT, CH, etc.)
            const cleanedNumber = digitsOnly.startsWith('0')
              ? digitsOnly.slice(1)
              : digitsOnly;
            const modifiedValue = '+' + dialCode + cleanedNumber;
            emitChange(modifiedValue);
          } else {
            emitChange(pastedText);
          }
        }
      }
    },
    [value.countryCode, emitChange, onChange]
  );

  return (
    <InternationalPhoneLayout
      className={className}
      variant={variant}
      size={size}
      {...focusWithin.focusWithinProps}
    >
      <CountryInput
        formProps={{
          value: value.countryCode,
          onChange: handleCountryChange,
          disabled
        }}
        variant={variant}
        size={size}
        hasError={hasError}
        readOnly={readOnly}
        showCallingCode={false}
      />
      <PhoneInput
        formProps={{
          value: value.rawValue,
          onChange: emitChange,
          disabled,
          name
        }}
        variant={variant}
        size={size}
        hasError={hasError}
        placeholder={placeholder}
        readOnly={readOnly}
        className="flex-1"
        onPaste={handlePaste}
        fallbackCountry={value.countryCode}
      />
    </InternationalPhoneLayout>
  );
}

/**
 * Converts a raw phone number string to a structured value object.
 * The structured value object contains the country code and the raw phone number.
 * @param rawValue - The raw phone number string.
 * The raw phone number is the phone number with or without the country code, depending on if the user entered it or if we inserted it on country select.
 * @param countryCode - The country code of the phone number.
 * @returns The structured value object. If the raw phone contains a valid country code, it will be returned, otherwise the country code will be returned.
 */
function toStructuredValue(
  rawValue: string,
  countryCode: Maybe<CountryCode>
): InternationalPhoneValue {
  const trimmed = rawValue.trim();

  try {
    const parsed = parsePhoneNumberWithError(trimmed, countryCode ?? undefined);

    return {
      countryCode: parsed.country ?? countryCode ?? null,
      rawValue: trimmed
    };
  } catch {
    return {
      countryCode: countryCode ?? null,
      rawValue: trimmed
    };
  }
}
